<script setup lang="ts">
import type { Query } from '@directus/types'
import Autoplay from 'embla-carousel-autoplay'

definePageMeta({
  title: 'Cable Assemblies & Harnesses',
  order: 0,
  description: 'Precision-engineered custom cable assemblies and harnesses backed by 50 years of experience.',
})

type ServiceGallery = Pick<Gallery, 'images'> & {
  images: Pick<GalleryFile, 'directus_files_id'> & {
    directus_files_id: Pick<DirectusFile, 'id' | 'title' | 'description'>
  }[]
}

const query: Query = {
  fields: [
    'images.directus_files_id.id',
    'images.directus_files_id.title',
    'images.directus_files_id.description',
  ],
}

const { data: gallery } = await useFetch(
  '/api/items/galleries/cable-assemblies',
  {
    key: 'page:cable-assemblies:gallery',
    query,
    lazy: true,
    transform: (gallery: ServiceGallery) => gallery.images.map(image => image.directus_files_id),
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)
</script>

<template>
  <section>
    <BlockContent>
      <h1 class="subpage-title">
        Precision-Engineered Custom Cable Assemblies and&nbsp;Harnesses
      </h1>
      <h2 class="subpage-subtitle mt-2">
        Backed by fifty years experience in cable assembly manufacturing
      </h2>
      <ul class="my-18 flex flex-wrap gap-y-8 f-text-16-20 px-card f-gap-x-44-64">
        <div class="space-y-8">
          <li class="flex gap-4 items-center">
            <div class="i-carbon-lightning inline-block scale-120 bg-gradient-icsbluecyan" />Quick-turn prototyping and competitive solutions
          </li>
          <li class="flex gap-4 items-center">
            <div class="i-carbon-gem inline-block scale-120 bg-gradient-icsbluecyan" />Serious build quality
          </li>
          <!-- <li class="flex items-center gap-4">
          <div class="i-carbon-delivery inline-block scale-120 bg-gradient-icsbluecyan" />100% on-time deliveries
        </li> -->
          <li class="flex gap-4 items-center">
            <div class="i-carbon-currency-dollar inline-block scale-120 bg-gradient-icsbluecyan" />Competitive pricing
          </li>
          <li class="flex gap-4 items-center">
            <div class="i-carbon-assembly inline-block scale-120 bg-gradient-icsbluecyan" />High-tech facility
          </li>
        </div>
        <li class="space-y-4">
          <div class="flex gap-4 items-center">
            <div class="i-carbon-intent-request-scale-out inline-block scale-120 bg-gradient-icsbluecyan" />Diverse capabilities, including:
          </div>
          <ul class="text-icssilver-800 leading-7 pl-10 list-square list-inside f-text-14-17 dark:text-icssilver-300 marker:text-icsgray-500">
            <li>MS connector & crimping applications</li>
            <li>Discrete wire harnesses</li>
            <li>Complex routed harnesses</li>
            <li>Jacketed cable assemblies</li>
            <li>Molded cable assemblies</li>
            <li>Jumper cable assemblies</li>
            <li>SCSI & RJ45 cable assemblies</li>
            <!-- eslint-disable-next-line -->
            <li><NuxtLink to="/capabilities" class="link">More</NuxtLink></li>
          </ul>
        </li>
      </ul>
      <nav class="mb-10 flex flex-wrap gap-6">
        <AButton
          to="/contact/rfq"
          label="Get a Quote"
          size="xlarge"
          color="cyan"
        />
        <AButton
          label="Full Electro-Mechanical Assemblies"
          to="/services/electromech-assemblies"
          size="xlarge"
          color="blue"
          icon="i-carbon-arrow-right "
          icon-side="right"
          material="empty"
          class="max-sm:w-full"
          body-class="justify-start"
          label-class="max-sm:whitespace-normal max-sm:text-left"
        />
      </nav>
    </BlockContent>
    <div class="py-10">
      <BlockContent>
        <h4 class="text-xl heading-strong drop-shadow dark:mb-4">
          Built by ICS:
        </h4>
      </BlockContent>
      <ACarousel
        :plugins="[Autoplay({ delay: 8000 })]"
        nav
        :clip="false"
        use-slide-css
        class="items-center"
      >
        <div
          v-for="item in gallery"
          :key="item.id"
          class="shrink-0 grow-0 basis-full max-w-full min-w-0 f-px-24-48 2xl:basis-1/3 4xl:basis-1/4 md:basis-1/2"
        >
          <NuxtImg
            :src="item.id"
            :alt="item.description || item.title || 'Custom ICS cable assembly.'"
            class="rounded-xs object-contain size-full dark:opacity-90"
          />
        </div>
      </ACarousel>
    </div>
  </section>
</template>
